import type {
  CmmdClaudeRuntimeReadiness,
  CmmdOrgProviderPolicy,
  ProviderKind,
  ServerProvider,
} from "@cmmd-forge/contracts";
import { isServerProviderExecutable } from "@cmmd-forge/shared/providerExecution";
import { WORKSPACE_PROVIDER_ACCESS_DISABLED } from "@cmmd-forge/shared/workspaceProviderAccess";

import { isOfferableProvider, isWalletAllowed } from "../providerPickerAvailability";
import type {
  ForgeProviderAccessState,
  PersonalProviderConnections,
  PersonalProviderReconnectRequired,
  ProviderCredentialMode,
} from "./forgeProviderAccess";

type PersonalProviderKind = Extract<ProviderKind, "codex" | "claudeAgent">;
type CloudCredentialStage = "live-runtime" | "provisioning";

export type ProviderIneligibilityReason =
  | "access-loading"
  | "access-error"
  | "not-offered"
  | "personal-credential-required"
  | "personal-credential-reconnect-required"
  | "native-runtime-not-ready"
  /**
   * CMMD says the Environment itself cannot take a credential delivery (boot
   * failed, or a status that accepts no owner request). Distinct from
   * `runtime-unavailable`, which the Shell derives from an absent or
   * non-executable snapshot and deliberately declines to name; this one is a
   * fact CMMD asserted about the runtime row (CMMD-36473).
   */
  | "environment-not-running"
  | "runtime-unavailable";

export type ProviderEligibility =
  | { readonly eligible: true }
  | { readonly eligible: false; readonly reason: ProviderIneligibilityReason };

export function resolveProviderEligibility(input: {
  readonly provider: ServerProvider | undefined;
  readonly providerKind: ProviderKind;
  readonly access: ForgeProviderAccessState;
  readonly credentialMode: ProviderCredentialMode;
}): ProviderEligibility {
  if (input.credentialMode === "runtime") {
    return input.provider && isServerProviderExecutable(input.provider)
      ? { eligible: true }
      : { eligible: false, reason: "runtime-unavailable" };
  }
  if (input.access.status === "loading") {
    return { eligible: false, reason: "access-loading" };
  }
  if (input.access.status === "error") {
    return { eligible: false, reason: "access-error" };
  }
  return resolveReadyProviderEligibility({
    provider: input.provider,
    providerKind: input.providerKind,
    policy: input.access.policy,
    personalConnections: input.access.personalConnections,
    personalReconnectRequired: input.access.personalReconnectRequired,
    claudeRuntimeReadiness: input.access.claudeRuntimeReadiness,
  });
}

function resolveReadyProviderEligibility(input: {
  readonly provider: ServerProvider | undefined;
  readonly providerKind: ProviderKind;
  readonly policy: CmmdOrgProviderPolicy | null;
  readonly personalConnections: PersonalProviderConnections | null;
  readonly personalReconnectRequired: PersonalProviderReconnectRequired | undefined;
  readonly claudeRuntimeReadiness: CmmdClaudeRuntimeReadiness | null;
}): ProviderEligibility {
  if (!input.provider || !isServerProviderExecutable(input.provider)) {
    return { eligible: false, reason: "runtime-unavailable" };
  }
  if (input.policy === null) {
    return { eligible: false, reason: "access-error" };
  }
  if (!isOfferableProvider(input.providerKind, input.policy)) {
    return { eligible: false, reason: "not-offered" };
  }

  return resolveCloudCredentialEligibility({
    providerKind: input.providerKind,
    policy: input.policy,
    personalConnections: input.personalConnections,
    personalReconnectRequired: input.personalReconnectRequired,
    claudeRuntimeReadiness: input.claudeRuntimeReadiness,
    stage: "live-runtime",
  });
}

/** Whether CMMD can provision a provider for a fresh cloud project. */
export function resolveProvisioningProviderEligibility(input: {
  readonly providerKind: ProviderKind;
  readonly access: ForgeProviderAccessState;
}): ProviderEligibility {
  if (input.access.status === "loading") {
    return { eligible: false, reason: "access-loading" };
  }
  if (input.access.status === "error" || input.access.policy === null) {
    return { eligible: false, reason: "access-error" };
  }
  if (!isOfferableProvider(input.providerKind, input.access.policy)) {
    return { eligible: false, reason: "not-offered" };
  }

  return resolveCloudCredentialEligibility({
    providerKind: input.providerKind,
    policy: input.access.policy,
    personalConnections: input.access.personalConnections,
    personalReconnectRequired: input.access.personalReconnectRequired,
    claudeRuntimeReadiness: input.access.claudeRuntimeReadiness,
    stage: "provisioning",
  });
}

function resolveCloudCredentialEligibility(input: {
  readonly providerKind: ProviderKind;
  readonly policy: CmmdOrgProviderPolicy;
  readonly personalConnections: PersonalProviderConnections | null;
  readonly personalReconnectRequired: PersonalProviderReconnectRequired | undefined;
  readonly claudeRuntimeReadiness: CmmdClaudeRuntimeReadiness | null;
  readonly stage: CloudCredentialStage;
}): ProviderEligibility {
  if (input.providerKind === "cmmd" || input.providerKind === "apex") {
    return { eligible: true };
  }
  if (input.providerKind !== "codex" && input.providerKind !== "claudeAgent") {
    return { eligible: false, reason: "not-offered" };
  }
  return resolvePersonalProviderEligibility({
    providerKind: input.providerKind,
    personalConnections: input.personalConnections,
    personalReconnectRequired: input.personalReconnectRequired,
    claudeRuntimeReadiness: input.claudeRuntimeReadiness,
    workspaceWalletAllowed: isWalletAllowed("workspace", input.policy),
    stage: input.stage,
  });
}

function resolvePersonalProviderEligibility(input: {
  readonly providerKind: PersonalProviderKind;
  readonly personalConnections: PersonalProviderConnections | null;
  readonly personalReconnectRequired: PersonalProviderReconnectRequired | undefined;
  readonly claudeRuntimeReadiness: CmmdClaudeRuntimeReadiness | null;
  readonly workspaceWalletAllowed: boolean;
  readonly stage: CloudCredentialStage;
}): ProviderEligibility {
  if (canUseWorkspaceProvider(input.workspaceWalletAllowed)) return { eligible: true };
  if (requiresCodexReconnect(input.providerKind, input.personalReconnectRequired)) {
    return { eligible: false, reason: "personal-credential-reconnect-required" };
  }
  if (!personalConnectionFor(input.providerKind, input.personalConnections)) {
    return { eligible: false, reason: "personal-credential-required" };
  }
  if (isEnvironmentNotRunning(input)) {
    return { eligible: false, reason: "environment-not-running" };
  }
  if (!isConnectedPersonalProviderReady(input)) {
    return { eligible: false, reason: "native-runtime-not-ready" };
  }
  return { eligible: true };
}

function canUseWorkspaceProvider(workspaceWalletAllowed: boolean): boolean {
  return workspaceWalletAllowed && !WORKSPACE_PROVIDER_ACCESS_DISABLED;
}

function requiresCodexReconnect(
  providerKind: PersonalProviderKind,
  reconnectRequired: PersonalProviderReconnectRequired | undefined,
): boolean {
  return providerKind === "codex" && reconnectRequired?.codex === true;
}

function personalConnectionFor(
  provider: PersonalProviderKind,
  connections: PersonalProviderConnections | null,
): boolean {
  return connections?.[provider] === true;
}

function isConnectedPersonalProviderReady(input: {
  readonly providerKind: PersonalProviderKind;
  readonly claudeRuntimeReadiness: CmmdClaudeRuntimeReadiness | null;
  readonly stage: CloudCredentialStage;
}): boolean {
  if (input.providerKind !== "claudeAgent" || input.stage === "provisioning") return true;
  return isNativeClaudeRuntimeReady(input.claudeRuntimeReadiness);
}

/**
 * The reason CMMD-36473 answers when the runtime row cannot take a delivery.
 * Read before the turn check: the answer keeps the row's real delivery
 * status, which may still say pending, and pending would otherwise read as a
 * credential on its way.
 */
const RUNTIME_UNAVAILABLE_READINESS_REASON = "runtime_unavailable";

function isEnvironmentNotRunning(input: {
  readonly providerKind: PersonalProviderKind;
  readonly claudeRuntimeReadiness: CmmdClaudeRuntimeReadiness | null;
  readonly stage: CloudCredentialStage;
}): boolean {
  if (input.providerKind !== "claudeAgent" || input.stage === "provisioning") return false;
  return input.claudeRuntimeReadiness?.reason === RUNTIME_UNAVAILABLE_READINESS_REASON;
}

function isNativeClaudeRuntimeReady(readiness: CmmdClaudeRuntimeReadiness | null): boolean {
  if (!claudeReadinessAllowsTurn(readiness)) return false;
  const identity = readiness.deliveryReceipt?.identity;
  return (
    identity?.kind === "credential_generation" &&
    identity.provider === "claude" &&
    identity.credentialGenerationId === readiness.generationId
  );
}

/**
 * Whether CMMD allows a turn to start on this runtime. Not keyed on the
 * delivery status: a refresh that failed over a credential the guest can still
 * run reads as failed with nativeExecutable true (FOR-3085). The receipt
 * identity above still binds it to this generation.
 */
function claudeReadinessAllowsTurn(
  readiness: CmmdClaudeRuntimeReadiness | null,
): readiness is CmmdClaudeRuntimeReadiness & { readonly generationId: string } {
  return (
    readiness?.connected === true &&
    readiness.nativeExecutable === true &&
    typeof readiness.generationId === "string" &&
    readiness.generationId.trim().length > 0
  );
}

export function resolveEligibleProviderSnapshots(input: {
  readonly providers: ReadonlyArray<ServerProvider>;
  readonly access: ForgeProviderAccessState;
  readonly credentialMode: ProviderCredentialMode;
}): ReadonlyArray<ServerProvider> {
  return input.providers.filter(
    (provider) =>
      resolveProviderEligibility({
        provider,
        providerKind: provider.provider,
        access: input.access,
        credentialMode: input.credentialMode,
      }).eligible,
  );
}

export function hasPotentialProviderAuthority(access: ForgeProviderAccessState): boolean {
  if (access.status !== "ready") return false;
  if (access.policy === null) return true;

  const offered = access.policy.providers;
  const workspaceWalletAllowed = isWalletAllowed("workspace", access.policy);
  if (offered.cmmd) return true;
  if (
    workspaceWalletAllowed &&
    !WORKSPACE_PROVIDER_ACCESS_DISABLED &&
    (offered.codex || offered.claude)
  ) {
    return true;
  }
  const personalAuthCouldRun =
    isWalletAllowed("personal", access.policy) ||
    (workspaceWalletAllowed && WORKSPACE_PROVIDER_ACCESS_DISABLED);
  if (!personalAuthCouldRun) return false;
  return (
    (offered.codex && access.personalConnections?.codex === true) ||
    (offered.claude && access.personalConnections?.claudeAgent === true)
  );
}
